import { twMerge } from 'tailwind-merge'
import { useEffect, useState } from 'react'

import { api } from '../services/api.service'

import { SliderField, TextAreaField, TextField } from '../forms/fields'
import FormModal from '../forms/form-modal'

import { style } from '../style'

export default function LostModal({ id, setId, refresh }) {
    const [ownership, setOwnership] = useState()
    const [userData, setUserData] = useState()
    const [changedFields, setChangedFields] = useState(new Set())

    useEffect(() => {
        if (!id) return

        // Reset changed fields when opening modal for new item
        setChangedFields(new Set())
        setOwnership(undefined)
        setUserData(undefined)

        let ownershipData = null
        let userDataResult = null

        const loadOwnership = api(`${process.env.REACT_APP_API_URL}/app/ownership/${id}`).then((x) => {
            ownershipData = x
            return x
        })

        const loadUserData = api(`${process.env.REACT_APP_API_URL}/app/profile`).then((x) => {
            userDataResult = x
            setUserData(x)
            return x
        })

        Promise.all([loadOwnership, loadUserData]).then(() => {
            // Initialize returnInstructions with default if it's empty/null/undefined
            const initializedOwnership = {
                ...ownershipData,
                returnInstructions: ownershipData?.returnInstructions || userDataResult?.defaultReturnInstructions || '',
            }
            setOwnership(initializedOwnership)
        })
    }, [id])

    const submitMarkAsLost = () => {
        api(`${process.env.REACT_APP_API_URL}/app/mark-as-lost/${id}`, { ...ownership }).then((x) => {
            refresh()
            setId(null)
        })
    }

    const handleCheckboxChange = (field, subField, value, setValues) => {
        setValues((prev) => ({
            ...prev,
            temporaryOverridePermissions: {
                ...prev.temporaryOverridePermissions,
                [field]: {
                    ...prev.temporaryOverridePermissions?.[field],
                    [subField]: value,
                },
            },
        }))
    }

    return (
        <FormModal open={!!id} setOpen={() => setId(null)}>
            <div className='w-full max-w-6xl p-5 text-sm bg-white rounded-md shadow sm:text-base max-h-[80dvh] overflow-auto'>
                <div className='mb-2 text-neutral-800'>
                    We all lose things, don't worry!
                    <br />
                    We're sure your item will find it's way back soon.
                </div>
                <div className='mb-2 text-neutral-800'>Just add your preferred contact details below so the finder can get in touch.</div>
                <div className='mb-2 text-neutral-800'>If you'd rather not share that information, they can also send you an message via Boomerang with return details. If that's the case, just make sure we're an authorised sender in your contacts, so that our email doesn't end up in your junk folder.</div>
                <TextAreaField
                    rows={2}
                    value={ownership?.returnInstructions !== undefined ? ownership?.returnInstructions : ''}
                    onChange={(val) => {
                        setChangedFields((prev) => new Set(prev).add('returnInstructions'))
                        setOwnership((x) => ({ ...x, returnInstructions: val }))
                    }}
                    label='Return Instructions'
                />

                <button className={twMerge(style.button.classname, style.button._secondary.classname, 'mt-2')} onClick={submitMarkAsLost}>
                    Submit
                </button>
                <div className='mt-5 text-left'>
                    <SettingsOverride userData={userData} handleCheckboxChange={handleCheckboxChange} values={ownership} setValues={setOwnership} changedFields={changedFields} setChangedFields={setChangedFields} />
                </div>

                {/* Finder Communication Settings */}
                <div className='pt-5 mt-5 border-t'>
                    <div className='mb-3 text-lg font-medium text-neutral-800'>Finder Communication</div>
                    <div className='mb-4 text-sm text-gray-600'>{userData?.subscriptionType === 'standard' ? 'Upgrade to Plus for GPS location sharing and masked phone calls.' : 'Control how finders can communicate with you while this item is lost.'}</div>

                    <div className='space-y-4'>
                        <div>
                            <h4 className='mb-2 font-medium text-neutral-700'>Allow finder to share GPS location</h4>
                            <div className='flex items-center mb-2 space-x-2'>
                                <input
                                    type='checkbox'
                                    id='temp-override-gps'
                                    checked={ownership?.temporaryOverrideAllowFinderGPS !== undefined ? ownership?.temporaryOverrideAllowFinderGPS : ownership?.overrideAllowFinderGPS !== undefined ? ownership?.overrideAllowFinderGPS : userData?.allowFinderGPS !== false}
                                    onChange={(e) => setOwnership((prev) => ({ ...prev, temporaryOverrideAllowFinderGPS: e.target.checked }))}
                                    disabled={userData?.subscriptionType === 'standard'}
                                    className='rounded border-gray-300 text-brand-600 focus:ring-brand-500'
                                />
                                <label htmlFor='temp-override-gps' className='text-sm text-gray-700'>
                                    {ownership?.temporaryOverrideAllowFinderGPS !== undefined ? (ownership?.temporaryOverrideAllowFinderGPS ? 'Enabled' : 'Disabled') : ownership?.overrideAllowFinderGPS !== undefined ? (ownership?.overrideAllowFinderGPS ? 'Using item setting: Enabled' : 'Using item setting: Disabled') : `Using default: ${userData?.allowFinderGPS !== false ? 'Enabled' : 'Disabled'}`}
                                </label>
                            </div>
                        </div>

                        <div>
                            <h4 className='mb-2 font-medium text-neutral-700'>Allow masked phone calls</h4>
                            <div className='flex items-center mb-2 space-x-2'>
                                <input
                                    type='checkbox'
                                    id='temp-override-calls'
                                    checked={ownership?.temporaryOverrideAllowMaskedCalls !== undefined ? ownership?.temporaryOverrideAllowMaskedCalls : ownership?.overrideAllowMaskedCalls !== undefined ? ownership?.overrideAllowMaskedCalls : userData?.allowMaskedCalls !== false}
                                    onChange={(e) => setOwnership((prev) => ({ ...prev, temporaryOverrideAllowMaskedCalls: e.target.checked }))}
                                    disabled={userData?.subscriptionType === 'standard'}
                                    className='rounded border-gray-300 text-brand-600 focus:ring-brand-500'
                                />
                                <label htmlFor='temp-override-calls' className='text-sm text-gray-700'>
                                    {ownership?.temporaryOverrideAllowMaskedCalls !== undefined ? (ownership?.temporaryOverrideAllowMaskedCalls ? 'Enabled' : 'Disabled') : ownership?.overrideAllowMaskedCalls !== undefined ? (ownership?.overrideAllowMaskedCalls ? 'Using item setting: Enabled' : 'Using item setting: Disabled') : `Using default: ${userData?.allowMaskedCalls !== false ? 'Enabled' : 'Disabled'}`}
                                </label>
                            </div>
                        </div>

                        {userData?.subscriptionType === 'standard' && (
                            <div className='p-3 bg-blue-50 rounded-md border border-blue-200'>
                                <p className='text-sm text-blue-800'>
                                    <strong>Plus Benefits:</strong> Upgrade to Plus for GPS location sharing and masked phone calls!
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </FormModal>
    )
}

const SettingsOverride = ({ values, setValues, userData, handleCheckboxChange, changedFields, setChangedFields }) => {
    if (!values || !userData) return null

    const fields = [
        { label: 'First Name', field: 'name', temporaryField: 'temporaryOverrideName', overrideField: 'overrideName', isOverride: true },
        { label: 'Last Name', field: 'lastName', temporaryField: 'temporaryOverrideLastName', overrideField: 'overrideLastName', isOverride: true },
        { label: 'Email', field: 'email', temporaryField: 'temporaryCustomEmail', overrideField: 'customEmail', isOverride: true },
        { label: 'Phone Number', field: 'phoneNumber', temporaryField: 'temporaryCustomPhoneNumber', overrideField: 'customPhoneNumber', isOverride: true },
        { label: 'WhatsApp', field: 'whatsappNumber', temporaryField: 'temporaryCustomWhatsappNumber', overrideField: 'customWhatsappNumber', isOverride: true },
    ]

    return (
        <div className='flex flex-col space-y-10 divide-y' key={JSON.stringify(values?.temporaryOverridePermissions)}>
            {fields.map(({ label, field, temporaryField, overrideField, customField, isOverride }) => {
                const isMutable = isOverride ? !!temporaryField : true
                const fieldKey = isOverride ? temporaryField : customField
                const hasBeenChanged = changedFields.has(fieldKey)

                let value
                if (hasBeenChanged) {
                    // User has explicitly changed this field, use their value (even if empty)
                    value = isOverride ? values[temporaryField] ?? '' : values[customField] ?? ''
                } else {
                    // Field hasn't been changed, show defaults
                    if (isOverride) {
                        // For override fields, check temporary -> override -> userData default
                        // Treat empty strings as "not set" unless user has changed them
                        if (values[temporaryField] !== undefined && values[temporaryField] !== '') {
                            value = values[temporaryField]
                        } else if (values[overrideField] !== undefined && values[overrideField] !== null && values[overrideField] !== '') {
                            value = values[overrideField]
                        } else {
                            value = userData[field] || ''
                        }
                    } else {
                        // For custom fields, check custom -> userData default
                        if (values[customField] !== undefined && values[customField] !== null && values[customField] !== '') {
                            value = values[customField]
                        } else {
                            value = userData[field] || ''
                        }
                    }
                }

                const permissionValue = values.temporaryOverridePermissions?.[field]?.showWhenLost !== undefined ? values.temporaryOverridePermissions?.[field]?.showWhenLost : values.overridePermissions?.[field]?.showWhenLost !== undefined ? values.overridePermissions?.[field]?.showWhenLost || values.overridePermissions?.[field]?.showAlways : userData.defaultPermissions?.[field]?.showWhenLost || userData.defaultPermissions?.[field]?.showAlways

                return (
                    <div className='flex space-x-5' key={field}>
                        <div className='flex-1'>
                            {isMutable ? (
                                <div>
                                    <TextField
                                        label={label}
                                        value={value}
                                        placeholder={!isOverride ? `Default: ${userData?.[field] || 'Not set'}` : ''}
                                        onChange={(val) => {
                                            const fieldKey = isOverride ? temporaryField : customField
                                            setChangedFields((prev) => new Set(prev).add(fieldKey))
                                            setValues((prev) => ({
                                                ...prev,
                                                [fieldKey]: val,
                                            }))
                                        }}
                                    />
                                    {['phoneNumber', 'whatsappNumber'].includes(field) && <p className='text-sm text-gray-500'>Mobile numbers should include a country code (e.g. +44)</p>}
                                    {field === 'whatsappNumber' && (
                                        <button
                                            type='button'
                                            className='px-2 py-1 mt-1 text-xs text-gray-700 bg-gray-100 rounded hover:bg-gray-200'
                                            onClick={() => {
                                                const phoneValue = values.temporaryCustomPhoneNumber || values.customPhoneNumber || userData?.phoneNumber
                                                setValues((prev) => ({ ...prev, temporaryCustomWhatsappNumber: phoneValue }))
                                            }}
                                        >
                                            Copy from phone number
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <div>
                                    <div>{label}</div>
                                    <div>{value}</div>
                                </div>
                            )}
                        </div>
                        <div>
                            <SliderField label='Show?' value={permissionValue} onChange={(val) => handleCheckboxChange(field, 'showWhenLost', val, setValues)} />
                        </div>
                    </div>
                )
            })}
        </div>
    )
}
